const memoryDbs = new Map();

const hasIndexedDb = () =>
  typeof indexedDB !== "undefined" && typeof indexedDB.open === "function";

const hasLocalStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const storageKey = (dbName, storeName) => `radtool:${dbName}:${storeName}`;

const loadMemoryStore = (dbName, storeName) => {
  if (!hasLocalStorage()) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(storageKey(dbName, storeName));
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("Failed to read local store fallback", error);
    return [];
  }
};

const persistMemoryStore = (dbName, storeName, values) => {
  if (!hasLocalStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(storageKey(dbName, storeName), JSON.stringify(values));
  } catch (error) {
    console.warn("Failed to persist local store fallback", error);
  }
};

const ensureMemoryStore = (dbName, storeName) => {
  let dbStores = memoryDbs.get(dbName);
  if (!dbStores) {
    dbStores = new Map();
    memoryDbs.set(dbName, dbStores);
  }
  if (!dbStores.has(storeName)) {
    const initial = loadMemoryStore(dbName, storeName);
    const store = new Map();
    initial.forEach((item) => {
      if (item?.id) {
        store.set(item.id, item);
      }
    });
    dbStores.set(storeName, store);
  }
  return dbStores.get(storeName);
};

export const openDb = (name) =>
  new Promise((resolve, reject) => {
    if (!hasIndexedDb()) {
      ensureMemoryStore(name, "rads");
      resolve({ name, __memory: true });
      return;
    }

    const request = indexedDB.open(name, 1);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains("rads")) {
        db.createObjectStore("rads", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("rad_changes")) {
        db.createObjectStore("rad_changes", { keyPath: "id", autoIncrement: true });
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(request.error || new Error("Failed to open IndexedDB"));
    };
  });

export const getAll = (db, store) =>
  new Promise((resolve, reject) => {
    if (!db) {
      resolve([]);
      return;
    }

    if (db.__memory) {
      const memoryStore = ensureMemoryStore(db.name, store);
      resolve(Array.from(memoryStore.values()));
      return;
    }

    let tx;
    try {
      tx = db.transaction(store, "readonly");
    } catch (error) {
      reject(error);
      return;
    }
    const objectStore = tx.objectStore(store);
    const request = objectStore.getAll();
    request.onsuccess = () => {
      resolve(Array.isArray(request.result) ? request.result : []);
    };
    request.onerror = () => {
      reject(request.error || new Error("Failed to read from IndexedDB"));
    };
  });

export const putItem = (db, store, item) =>
  new Promise((resolve, reject) => {
    if (!db) {
      resolve();
      return;
    }

    if (db.__memory) {
      const memoryStore = ensureMemoryStore(db.name, store);
      if (!item?.id) {
        reject(new Error("Item must include an id property"));
        return;
      }
      memoryStore.set(item.id, { ...item });
      persistMemoryStore(db.name, store, Array.from(memoryStore.values()));
      resolve();
      return;
    }

    let tx;
    try {
      tx = db.transaction(store, "readwrite");
    } catch (error) {
      reject(error);
      return;
    }
    const objectStore = tx.objectStore(store);
    const request = objectStore.put(item);
    request.onsuccess = () => resolve();
    request.onerror = () => {
      reject(request.error || new Error("Failed to write to IndexedDB"));
    };
  });

export const bulkPut = (db, store, items) =>
  new Promise((resolve, reject) => {
    if (!db) {
      resolve();
      return;
    }

    if (db.__memory) {
      const memoryStore = ensureMemoryStore(db.name, store);
      memoryStore.clear();
      (items || []).forEach((item) => {
        if (item?.id) {
          memoryStore.set(item.id, { ...item });
        }
      });
      persistMemoryStore(db.name, store, Array.from(memoryStore.values()));
      resolve();
      return;
    }

    let tx;
    try {
      tx = db.transaction(store, "readwrite");
    } catch (error) {
      reject(error);
      return;
    }
    const objectStore = tx.objectStore(store);
    objectStore.clear();
    (items || []).forEach((item) => {
      objectStore.put(item);
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => {
      reject(tx.error || new Error("Failed to persist records"));
    };
    tx.onabort = () => {
      reject(tx.error || new Error("IndexedDB transaction aborted"));
    };
  });

export const markSynced = (db, store, id) =>
  new Promise((resolve, reject) => {
    if (!db) {
      resolve();
      return;
    }

    if (db.__memory) {
      const memoryStore = ensureMemoryStore(db.name, store);
      if (memoryStore.has(id)) {
        const current = memoryStore.get(id);
        memoryStore.set(id, {
          ...current,
          synced: true,
          syncedAt: new Date().toISOString(),
        });
        persistMemoryStore(db.name, store, Array.from(memoryStore.values()));
      }
      resolve();
      return;
    }

    let tx;
    try {
      tx = db.transaction(store, "readwrite");
    } catch (error) {
      reject(error);
      return;
    }
    const objectStore = tx.objectStore(store);
    const request = objectStore.get(id);
    request.onsuccess = () => {
      const record = request.result;
      if (!record) {
        resolve();
        return;
      }
      const nextRecord = {
        ...record,
        synced: true,
        syncedAt: new Date().toISOString(),
      };
      const update = objectStore.put(nextRecord);
      update.onsuccess = () => resolve();
      update.onerror = () => {
        reject(update.error || new Error("Failed to mark record as synced"));
      };
    };
    request.onerror = () => {
      reject(request.error || new Error("Failed to load record for syncing"));
    };
  });
